import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { Link } from 'react-router-dom';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import CheckIcon from '@material-ui/icons/Check';

const styles = theme => ({
    root: {
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto',
        padding: `${theme.spacing.unit * 2}px ${theme.spacing.unit * 2}px ${theme.spacing.unit * 4}px`,
    },
    button: {
        margin: theme.spacing.unit,
    },
    card: {
        marginTop: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 2,
        borderRadius: theme.spacing.unit,
        boxShadow: '0 12px 32px rgba(17, 38, 146, 0.08)',
    },
    link: {
        textDecoration: 'none',
    },
    section: {
        marginTop: theme.spacing.unit * 3,
    },
    heroCard: {
        marginTop: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 3,
        borderRadius: theme.spacing.unit * 2,
        background: 'linear-gradient(135deg, #eef7ff 0%, #f9fcff 55%, #f1f4ff 100%)',
        boxShadow: '0 20px 40px rgba(17, 38, 146, 0.12)',
    },
    heroContent: {
        padding: theme.spacing.unit * 4,
    },
    lead: {
        maxWidth: 760,
    },
    sectionIntro: {
        maxWidth: 720,
    },
    apiLink: {
        color: theme.palette.primary.main,
        fontWeight: 500,
    },
});

const supportedFormats = [
    'Dataset-JSON v1.1',
    'NDJSON representation of Dataset-JSON 1.1',
    'Compressed Dataset-JSON 1.1',
    'XPORT v5',
    'SAS7BDAT',
    'SPSS SAV, ZSAV, POR',
    'Stata DTA',
];

const featureColumns = [
    [
        'Reading large size datasets',
        'Dataset compare',
        'Converter between formats',
        'Filtering with value autocomplete',
        'Sorting and fast row or column navigation',
    ],
    [
        'Metadata inspection',
        'API access',
        'UI for CDISC CORE',
        'Automatic updates and automated testing',
        'Multiplatform: Windows, Linux, MacOS',
    ],
];

function CheckList({ items }) {
    return (
        <List dense>
            {items.map(item => (
                <ListItem key={item}>
                    <ListItemIcon>
                        <CheckIcon />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                </ListItem>
            ))}
        </List>
    );
}

CheckList.propTypes = {
    items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function VdeDatasetViewer(props) {
    const { classes } = props;

    return (
        <div className={classes.root}>
            <Typography variant="h4" gutterBottom>
                VDE Dataset Viewer
            </Typography>

            <Card className={classes.heroCard}>
                <CardContent className={classes.heroContent}>
                    <Typography variant="h5" gutterBottom>
                        Explore clinical datasets with less friction
                    </Typography>
                    <Typography variant="body1" paragraph className={classes.lead} inline>
                        VDE Dataset Viewer is a desktop application for opening, inspecting, and navigating
                        datasets across common clinical data exchange formats.
                    </Typography>
                    <Typography variant="body1" className={classes.lead} inline>
                        Focuses on large dataset handling, side-by-side comparison, format conversion,
                        and CDISC-oriented review workflows in a cross-platform interface.
                    </Typography>
                </CardContent>
            </Card>

            <Grid container spacing={16} justify="center">
                <Grid item>
                    <Link to="/vde-dataset-viewer/downloads" className={classes.link}>
                        <Button variant="contained" color="primary" className={classes.button}>
                            Download
                        </Button>
                    </Link>
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        color="primary"
                        className={classes.button}
                        component="a"
                        href="https://github.com/defineEditor/vde-dataset-viewer"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        View on GitHub
                    </Button>
                </Grid>
            </Grid>

            <div className={classes.section}>
                <Typography variant="h5" gutterBottom>
                    Supported Formats
                </Typography>
                <Card className={classes.card}>
                    <CardContent>
                        <CheckList items={supportedFormats} />
                    </CardContent>
                </Card>
            </div>

            <div className={classes.section}>
                <Typography variant="h5" gutterBottom>
                    Features
                </Typography>
                <Card className={classes.card}>
                    <CardContent>
                        <Grid container spacing={16}>
                            <Grid item xs={12} sm={6}>
                                <CheckList items={featureColumns[0]} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <CheckList items={featureColumns[1]} />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </div>

            <div className={classes.section}>
                <Typography variant="h5" gutterBottom>
                    API Specification
                </Typography>
                <Typography variant="body1" paragraph>
                    <a className={classes.apiLink}
                       href="https://github.com/cdisc-org/DataExchange-DatasetJson-API"
                       target="_blank"
                       rel="noopener noreferrer">
                        DataExchange-DatasetJson-API
                    </a>
                </Typography>
            </div>

            <div className={classes.section}>
                <Typography variant="body1">
                    This project is licensed under the MIT License.
                </Typography>
            </div>
        </div>
    );
}

VdeDatasetViewer.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(VdeDatasetViewer);